/**
 * Authentication Service
 * Maneja todas las llamadas al API de autenticación
 */

import { API_BASE_URL } from '../constants';

const AUTH_API = `${API_BASE_URL}/api/auth`;

class AuthService {
    /**
     * Registrar un nuevo usuario
     * @param {string} email - Email del usuario
     * @param {string} password - Contraseña
     * @param {string} name - Nombre completo
     * @returns {Promise} - Usuario creado
     */
    async register(email, password, name, website = '') {
        try {
            const response = await fetch(`${AUTH_API}/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, name, website }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Registration failed');
            }

            return data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Iniciar sesión
     * @param {string} email - Email del usuario
     * @param {string} password - Contraseña
     * @returns {Promise} - Token y datos del usuario
     */
    async login(email, password) {
        try {
            const response = await fetch(`${AUTH_API}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }

            // Guardar token en localStorage
            if (data.token) {
                this.setToken(data.token);
                this.setUser(data.user);
            }

            return data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cerrar sesión
     */
    async logout() {
        try {
            // Opcional: llamar al endpoint de logout
            await fetch(`${AUTH_API}/logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.getToken()}`,
                },
            });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Limpiar localStorage
            this.removeToken();
            this.removeUser();
        }
    }

    /**
     * Verificar si el token es válido
     * @returns {Promise} - Datos del usuario si el token es válido
     */
    async verifyToken() {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No token found');
            }

            const response = await fetch(`${AUTH_API}/verify`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                // Attach status to error for handling
                const error = new Error(data.error || 'Token verification failed');
                error.status = response.status;
                throw error;
            }

            // Actualizar usuario en localStorage
            this.setUser(data.user);

            return data.user;
        } catch (error) {
            // Rethrow so AuthContext handles the logic
            throw error;
        }
    }

    /**
     * Obtener datos del usuario actual
     * @returns {Promise} - Datos del usuario
     */
    async getCurrentUser() {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No token found');
            }

            const response = await fetch(`${AUTH_API}/me`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to get user');
            }

            this.setUser(data.user);
            return data.user;
        } catch (error) {
            throw error;
        }
    }

    // ============================================================================
    // Métodos de LocalStorage
    // ============================================================================

    /**
     * Guardar token en localStorage
     */
    setToken(token) {
        localStorage.setItem('auth_token', token);
    }

    /**
     * Obtener token de localStorage
     */
    getToken() {
        return localStorage.getItem('auth_token');
    }

    /**
     * Eliminar token de localStorage
     */
    removeToken() {
        localStorage.removeItem('auth_token');
    }

    /**
     * Guardar usuario en localStorage
     */
    setUser(user) {
        localStorage.setItem('auth_user', JSON.stringify(user));
    }

    /**
     * Obtener usuario de localStorage
     */
    getUser() {
        const user = localStorage.getItem('auth_user');
        return user ? JSON.parse(user) : null;
    }

    /**
     * Eliminar usuario de localStorage
     */
    removeUser() {
        localStorage.removeItem('auth_user');
    }

}

const authService = new AuthService();
export default authService;
// Revisado: 2025-12-16
